import { Fragment } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { selectAuth } from "../redux/features/auth/auth-slice";
import DashboardLayout from "../shared/components/dashboard-layout/dashboard-layout";
import PublicRoute from "../shared/components/public-route/public-route";
import { EPrivileges } from "../shared/oversight-core/enums/privileges";
import { UserRole } from "../shared/oversight-core/enums/user-roles";
import { AppRoute } from "../shared/oversight-core/interfaces/app-routes";
import IPageInfo from "../shared/oversight-core/interfaces/page-info";
import NotFound from "../shared/oversight-core/shared-components/not-found/not-found";
import PrivateRoute from "../shared/oversight-core/shared-components/private-route/private-route";
import Unauthorized from "../shared/oversight-core/shared-components/unauthorized/unauthorized";
import ForgotPassword from "../shared/oversight-core/shared-pages/forgot-password/forgot-password";
import MobileNumberOtpVerification from "../shared/oversight-core/shared-pages/mobile-number-otp-verification/mobile-number-otp-verification";
import ResetPassword from "../shared/oversight-core/shared-pages/reset-password/reset-password";
import UserProfile from "../shared/oversight-core/shared-pages/user-profile/user-profile";
import RemoveTrailingSlash from "../shared/oversight-core/utils/removeTrailingSlash";
import AdminDashboard from "./dashboard/dashboard";
import AdminLogin from "./login/login";
import Sites from "./sites/sites";
import SitesErrorLog from "./sites/sites-error-log/sites-error-log";
import ViewSite from "./sites/view-site/view-site";

export const publicRoutes: IPageInfo[] = [
  {
    name: "Forgot Password",
    path: AppRoute.FORGOT_PASSWORD,
    component: <ForgotPassword />,
    enableRoute: true,
  },
  {
    name: "Reset Password",
    path: AppRoute.RESET_PASSWORD,
    component: <ResetPassword />,
    enableRoute: true,
  },
  {
    name: "Mobile Number Verification",
    path: AppRoute.MOBILE_NUMBER_VERIFICATION,
    component: <MobileNumberOtpVerification />,
    enableRoute: true,
  },
  {
    name: "Admin Login",
    path: AppRoute.ADMIN_LOGIN,
    component: <AdminLogin />,
    enableRoute: true,
  },
];

export const adminPrivateRoutes: IPageInfo[] = [
  {
    name: "Admin",
    path: AppRoute.ADMIN,
    component: <DashboardLayout />,
    root: true,
    subRoutes: [
      {
        name: "Dashboard",
        icon: "home",
        path: AppRoute.ADMIN_DASHBOARD,
        index: true,
        component: <AdminDashboard />,
        enableRoute: true,
        bgColor: "activeBg-1",
        bgColorIcon: "#1A3C84",
        showOnDrawer: true,
        privilege: EPrivileges.DASHBOARD_READ_PRIVILEGE,
      },
      {
        name: "Sites",
        icon: "sunny",
        path: AppRoute.ADMIN_SITES_ERROR_LOG,
        index: true,
        component: <SitesErrorLog />,
        enableRoute: true,
        bgColor: "activeBg-13",
        bgColorIcon: "#AC782B",
        showOnDrawer: false,
        privilege: EPrivileges.ROLE_SOLAR_DASHBOARD_ADMIN,
      },
      {
        name: "Sites",
        icon: "sunny",
        path: AppRoute.ADMIN_SITES,
        index: true,
        component: <Sites />,
        enableRoute: true,
        bgColor: "activeBg-13",
        bgColorIcon: "#AC782B",
        showOnDrawer: true,
        privilege: EPrivileges.ROLE_SOLAR_DASHBOARD_ADMIN,
      },
      {
        name: "Sites",
        icon: "sunny",
        path: AppRoute.ADMIN_SITES + "/:id",
        index: true,
        component: <ViewSite />,
        enableRoute: true,
        bgColor: "activeBg-13",
        bgColorIcon: "#AC782B",
        showOnDrawer: false,
        privilege: EPrivileges.ROLE_SOLAR_DASHBOARD_ADMIN,
      },
      // {
      //   name: "Estimations",
      //   icon: "solar_power",
      //   path: AppRoute.ADMIN_ESTIMATIONS,
      //   index: true,
      //   component: <Estimations />,
      //   enableRoute: true,
      //   bgColor: "activeBg-18",
      //   bgColorIcon: "#5031B0",
      //   showOnDrawer: true,
      // },
      // {
      //   name: "Estimations",
      //   icon: "solar_power",
      //   path: AppRoute.ADMIN_ESTIMATIONS + "/:id",
      //   index: true,
      //   component: <ViewEstimation />,
      //   enableRoute: true,
      //   bgColor: "activeBg-18",
      //   bgColorIcon: "#5031B0",
      //   showOnDrawer: false,
      // },
      // {
      //   name: "Settings",
      //   path: AppRoute.ADMIN_SETTINGS,
      //   icon: "settings",
      //   component: <AdminSettings />,
      //   enableRoute: true,
      //   bgColor: "activeBg-6",
      //   bgColorIcon: "##383941",
      //   showOnDrawer: true,
      // },
      {
        name: "Profile",
        path: AppRoute.ADMIN_PROFILE,
        component: <UserProfile />,
        enableRoute: true,
        showOnDrawer: false,
        privilege: EPrivileges.GENERAL_READ_PRIVILEGE,
      },
      {
        name: "404 Page Not Found",
        path: AppRoute.ADMIN_NOT_FOUND,
        component: <NotFound />,
        showOnDrawer: false,
      },
    ],
  },
];

const AppRoutes = () => {
  const auth = useSelector(selectAuth);
  const getRoutes = (routes: IPageInfo[]) => {
    return routes.map((privateRoutes: IPageInfo, index) => (
      <Route
        key={index}
        path={privateRoutes.path}
        element={privateRoutes.component}
      >
        {privateRoutes.subRoutes?.map((subRoute: IPageInfo, index) => {
          const route = [
            <Route
              key={index}
              path={subRoute.path}
              element={
                !subRoute.privilege ||
                auth.privileges.includes(subRoute.privilege) ? (
                  subRoute.component
                ) : (
                  <Unauthorized />
                )
              }
            />,
          ];

          if (subRoute.index) {
            route.push(
              <Route
                key={index + subRoute.path}
                index
                element={<Navigate to={subRoute.path} />}
              />
            );
          }
          return route;
        })}
      </Route>
    ));
  };

  return (
    <Fragment>
      <RemoveTrailingSlash />
      <Routes>
        <Route path="/" element={<PublicRoute />}>
          <Route index element={<Navigate to={AppRoute.ADMIN_LOGIN} />} />
          {publicRoutes.map((p: IPageInfo, index) => {
            return <Route key={index} path={p.path} element={p.component} />;
          })}
          <Route path="*" element={<Navigate to={AppRoute.ADMIN_LOGIN} />} />
        </Route>

        <Route
          path="/"
          element={
            <PrivateRoute
              role={[UserRole.ROLE_ADMIN, UserRole.ROLE_SOLAR_DASHBOARD_ADMIN]}
            />
          }
        >
          {getRoutes(adminPrivateRoutes)}
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />
      </Routes>
    </Fragment>
  );
};

export default AppRoutes;
